// workspaceSetup.js — THE WORKSPACE BUILDS ITSELF, in front of the person
// (merge programme lane WS1; Nika's decisions 9 and 11, 2026-10-25: the setup
// steps show IN the workspace while it builds itself, and the first workspace
// thread holds the plan, the summary of the site conversation and the first
// build).
//
// THE DATA IS THE SERVER'S (server lane S1, SDK `getWorkspaceSetup` /
// `startWorkspaceSetup`). Make it real calls `POST /core/workspaces/:id/setup`
// right before the hand-over; the job runs `name`, `language`, `theme`,
// `onboarding` and `thread` before it answers, then one `app:<owner/key>` step
// per app, `preset` and `website` in the background. `GET …/setup` answers
//   { id, run, rev, status: running | done | failed, reason?, stale,
//     steps: [{ key, status, at, reason?, detail? }], counts, threadId,
//     projectId, startedAt, finishedAt }   (or null; owner / admin only)
// and holds NO free text. The client computes nothing of the setup: every
// line, state and reason below is the answer's.
//
// WHERE IT SHOWS: the app builder (/add-app — Fly's hand-over lands there),
// in the job's first thread. The builder opens that thread (the `_ax_` scope,
// the build surface) and draws the setup above its messages
// (components/WorkspaceSetupThread.js): the plan card, one line per step (the
// apps in install order — each app reaches the navigation as its step turns
// done), the website card, the credits, one way back to the site the person
// came from, and the first build.
//
// WHEN: this tab arrived from Make it real (`?fly=` on the builder — the
// arrival mark below, kept for this tab), or the person's own onboarding
// record still says Fly owes them the ready screen (`pending` + `via: 'fly'`).
// No other boot asks. The job shows while it runs and for RECENT_MS after it
// finished; an older job is history (its thread stays a normal thread).
//
// THE POLL: while the job runs — 1 s, growing to 5 s, back to 1 s on every
// change; no request while the tab is hidden; it stops when the job settles,
// the workspace changes or after 15 minutes.
//
// THE FIRST BUILD (CORE r1, the first-build waiver): when the setup is done,
// the idea (`concepts[0].text`, else a line made from the plan) goes as the
// person's own message into the setup thread — seen as sent, never a hidden
// turn — through the builder's scope, so the turn carries the build surface
// (`context.surface: 'add-app-builder'`). It goes by itself only while the
// waiver is open (the signup policy says `firstBuildFree: true`, this person
// never claimed it, this workspace never used it). Otherwise the person
// presses Build (no server publishes what a build costs, so "enough" is never
// guessed), and a workspace with no credits shows the claim / top-up path
// first and sends the build when credits arrive. The client never decides who
// pays: the server's waiver and meter do.
//
// NEVER WRITTEN HERE: the onboarding record (the ready screen is its one
// writer — workspaceSetupHoldsReady tells it when to open), and no free text
// (the summary, the idea) in a URL, an event or a log line.
//
// FRANK: every export is self-contained (FA204) — the caps and the keys are
// restated where they are used, siblings through `this.call`. `this` is the
// calling element.

// The arrival — the builder's mount, BEFORE functions/addAppPrompt.js takes
// `fly` out of the address bar. Where the builder is the route
// (`opts.url !== false`), a `fly` param marks this tab: the mark ({ at, back })
// lives in this tab's sessionStorage so a reload inside RECENT_MS still finds
// it, and on the root for a page with no storage. `back` is the site the
// person came from (the referrer, when it is a platform host other than this
// one) — the way back. → true when the load is an arrival.
export function workspaceSetupArrive(root, opts) {
  const MARK = 'symbols_workspace_setup'
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object') return false
  if (opts && opts.url === false) return false
  const w = typeof window !== 'undefined' ? window : null
  if (!w || !w.location) return false
  let fly = false
  try {
    fly = new URLSearchParams(String(w.location.search || '')).has('fly')
  } catch (_) {
    fly = false
  }
  if (!fly) return false
  const fns = (el && el.context && el.context.functions) || {}
  let back = ''
  try {
    const doc = w.document
    const ref = doc && typeof doc.referrer === 'string' ? doc.referrer : ''
    const url = ref ? new URL(ref) : null
    const local = url && (url.hostname === 'localhost' || url.hostname.endsWith('.localhost'))
    const platform =
      url && typeof fns.isPlatformHost === 'function' && el.call('isPlatformHost', url.hostname)
    if (
      url &&
      (url.protocol === 'https:' || (local && url.protocol === 'http:')) &&
      url.origin !== w.location.origin &&
      platform
    ) {
      back = url.origin + url.pathname
    }
  } catch (_) {
    back = ''
  }
  const mark = { at: Date.now(), ws: '', back }
  try {
    const store = w.sessionStorage
    if (store) store.setItem(MARK, JSON.stringify(mark))
  } catch (_) {
    /* no storage: the root keeps the mark for this page */
  }
  r.__workspaceSetupArrival = mark
  return true
}

// Who asks the server: `arrived` — this tab's arrival mark is fresh (inside
// RECENT_MS) and not another workspace's; `owed` — the person's onboarding
// record says Fly still owes the ready screen. → { arrived, owed, back }
export function workspaceSetupWanted(root) {
  const MARK = 'symbols_workspace_setup'
  const RECENT_MS = 10 * 60 * 1000
  const r = root && typeof root === 'object' ? root : null
  const out = { arrived: false, owed: false, back: '' }
  if (!r) return out
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  let mark =
    r.__workspaceSetupArrival && typeof r.__workspaceSetupArrival === 'object'
      ? r.__workspaceSetupArrival
      : null
  try {
    const store = typeof window !== 'undefined' ? window.sessionStorage : null
    const raw = store ? store.getItem(MARK) : null
    const kept = raw ? JSON.parse(raw) : null
    if (kept && typeof kept === 'object' && Number(kept.at) >= Number((mark && mark.at) || 0)) {
      mark = kept
    }
  } catch (_) {
    /* the root's mark, if any */
  }
  const age = mark ? Date.now() - Number(mark.at) : NaN
  const ws = mark ? String(mark.ws || '') : ''
  out.arrived = Number.isFinite(age) && age >= 0 && age <= RECENT_MS && (!ws || ws === wsId)
  if (out.arrived) out.back = String(mark.back || '')
  // the hand-over still in the address bar (the builder has not mounted yet
  // and so has not marked it): an arrival all the same
  if (!out.arrived) {
    try {
      const loc = typeof window !== 'undefined' ? window.location : null
      out.arrived =
        !!loc &&
        /\/add-app\/?$/.test(String(loc.pathname || '')) &&
        new URLSearchParams(String(loc.search || '')).has('fly')
    } catch (_) {
      out.arrived = false
    }
  }
  const prefs = r.userPrefs && typeof r.userPrefs === 'object' ? r.userPrefs : null
  const rec =
    prefs && prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  out.owed = !!(rec && rec.status === 'pending' && rec.via === 'fly')
  return out
}

// THE READ — one `GET setup` for the active workspace, through the SDK only and
// only where it can answer: the workspace is ready, this person is its owner
// or admin (the route's own rule), the SDK has the read, and the setup is
// wanted (workspaceSetupWanted, or `opts.force`). One read in flight per
// workspace; an answer for a workspace that is no longer active drops itself.
// A 403 / 404 is "no setup here" (`none`), never an error on screen.
//   root._workspaceSetup = { ws, status: 'reading' | 'ok' | 'none' | 'error',
//     at, job, code, arrived, back, opened, opening, retrying, policy, site,
//     build, apps }
// → a promise of the stored entry, or null when nothing was asked.
export function workspaceSetupRead(root, opts) {
  const MARK = 'symbols_workspace_setup'
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object' || typeof r.update !== 'function') return Promise.resolve(null)
  const wsOf = (x) => {
    const w = x && x.workspace
    return String((w && (w.id || w._id)) || '')
  }
  const wsId = wsOf(r)
  if (!wsId || !r.workspaceReady) return Promise.resolve(null)
  const fns = (el && el.context && el.context.functions) || {}
  if (typeof fns.currentUserIsAdmin !== 'function' || !el.call('currentUserIsAdmin', r)) {
    return Promise.resolve(null)
  }
  const wanted = el.call('workspaceSetupWanted', r) || {}
  if (!o.force && !wanted.arrived && !wanted.owed) return Promise.resolve(null)
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  // The feature gate is the method itself (the shell's init-await proxy reads
  // a method the SDK lacks as undefined), never a version guess.
  if (!sdk || sdk.isNullClient === true || typeof sdk.getWorkspaceSetup !== 'function') {
    return Promise.resolve(null)
  }
  const flight = r.__workspaceSetupFlight
  if (flight && flight.ws === wsId && flight.promise) return flight.promise
  const prevOf = () =>
    r._workspaceSetup && typeof r._workspaceSetup === 'object' && r._workspaceSetup.ws === wsId
      ? r._workspaceSetup
      : null
  // The arrival is this workspace's from now on (a switch inside the window is
  // not an arrival for the next workspace).
  if (wanted.arrived) {
    try {
      const store = typeof window !== 'undefined' ? window.sessionStorage : null
      const raw = store ? store.getItem(MARK) : null
      const kept = raw ? JSON.parse(raw) : null
      if (kept && typeof kept === 'object' && !kept.ws) {
        store.setItem(MARK, JSON.stringify({ ...kept, ws: wsId }))
      }
    } catch (_) {
      /* the mark stays as it is */
    }
    if (r.__workspaceSetupArrival && !r.__workspaceSetupArrival.ws) {
      r.__workspaceSetupArrival = { ...r.__workspaceSetupArrival, ws: wsId }
    }
  }
  const base = () => {
    const prev = prevOf()
    return {
      ws: wsId,
      status: 'reading',
      at: 0,
      job: null,
      code: '',
      arrived: !!wanted.arrived || !!(prev && prev.arrived),
      back: (prev && prev.back) || wanted.back || '',
      opened: (prev && prev.opened) || '',
      opening: !!(prev && prev.opening),
      retrying: !!(prev && prev.retrying),
      policy: prev && prev.policy !== undefined ? prev.policy : undefined,
      me: (prev && prev.me) || null,
      site: (prev && prev.site) || null,
      build: (prev && prev.build) || '',
      apps: (prev && Array.isArray(prev.apps) && prev.apps) || null
    }
  }
  if (!prevOf()) r.update({ _workspaceSetup: base() }, { preventFetch: true })
  const clean = (job) => {
    const steps = (Array.isArray(job.steps) ? job.steps : [])
      .filter((x) => x && typeof x.key === 'string' && x.key)
      .map((x) => ({
        key: x.key,
        status: String(x.status || 'pending'),
        at: x.at || null,
        reason: typeof x.reason === 'string' ? x.reason : '',
        detail: x.detail && typeof x.detail === 'object' ? { ...x.detail } : null
      }))
    return {
      id: String(job.id || job._id || ''),
      run: job.run || 0,
      rev: job.rev || 0,
      status: String(job.status || ''),
      reason: typeof job.reason === 'string' ? job.reason : '',
      stale: !!job.stale,
      steps,
      threadId: job.threadId ? String(job.threadId) : '',
      projectId: job.projectId ? String(job.projectId) : '',
      startedAt: job.startedAt || null,
      finishedAt: job.finishedAt || null
    }
  }
  const promise = Promise.resolve()
    .then(() => sdk.getWorkspaceSetup(wsId))
    .then(
      (res) => {
        const bare = res && typeof res === 'object' && !Array.isArray(res) ? res : null
        const job =
          bare && 'success' in bare && 'data' in bare
            ? bare.data && typeof bare.data === 'object'
              ? bare.data
              : null
            : bare
        if (!job) return { status: 'none', job: null, code: '' }
        if (typeof job.status !== 'string' || !Array.isArray(job.steps)) {
          return { status: 'error', job: null, code: 'shape' }
        }
        return { status: 'ok', job: clean(job), code: '' }
      },
      (err) => {
        const code = String((err && (err.status || err.statusCode || err.code)) || 'failed')
        return code === '403' || code === '404'
          ? { status: 'none', job: null, code }
          : { status: 'error', job: null, code }
      }
    )
    .then((got) => {
      if (r.__workspaceSetupFlight && r.__workspaceSetupFlight.promise === promise) {
        r.__workspaceSetupFlight = null
      }
      if (wsOf(r) !== wsId) return null
      const next = { ...base(), ...got, at: Date.now() }
      // An earlier answer of this page stays on screen through one failed
      // poll (a network blip must not wipe the lines the person is reading).
      const prev = prevOf()
      if (got.status === 'error' && prev && prev.status === 'ok' && prev.job) {
        next.status = 'ok'
        next.job = prev.job
      }
      r.update({ _workspaceSetup: next }, { preventFetch: true })
      return next
    })
  r.__workspaceSetupFlight = { ws: wsId, promise }
  return promise
}

// THE POLL — reads while the job runs. One loop per workspace and page
// (`root.__workspaceSetupPoll`); a settled job, a workspace change, 15 minutes
// or three failed reads in a row end it. While the tab is hidden it asks
// nothing (it looks again every 5 s; the gate's visibility listener resumes it
// at once). `opts.force` starts a fresh loop (after a retry). After each answer
// the effects run (workspaceSetupEffects — the navigation, the website, the
// first build). → true when a loop runs.
export function workspaceSetupPoll(root, opts) {
  const BASE_MS = 1000
  const CAP_MS = 5000
  const GROW = 1.6
  const MAX_MS = 15 * 60 * 1000
  const FAILS = 3
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object' || !el || typeof el.call !== 'function') return false
  const wsOf = (x) => {
    const w = x && x.workspace
    return String((w && (w.id || w._id)) || '')
  }
  const wsId = wsOf(r)
  if (!wsId) return false
  const cur = r.__workspaceSetupPoll
  if (cur && cur.ws === wsId && !o.force) {
    if (cur.live) {
      // a hidden tab came back: ask now instead of at the slow look
      if (o.wake && cur.timer && cur.slept) {
        clearTimeout(cur.timer)
        cur.timer = setTimeout(cur.tick, 0)
      }
      return true
    }
    if (cur.done) return false
  }
  if (cur && cur.timer) clearTimeout(cur.timer)
  const poll = {
    ws: wsId,
    live: true,
    done: false,
    slept: false,
    since: Date.now(),
    delay: BASE_MS,
    sig: '',
    fails: 0,
    timer: null,
    tick: null
  }
  r.__workspaceSetupPoll = poll
  const end = (done) => {
    poll.live = false
    poll.done = !!done
    if (poll.timer) clearTimeout(poll.timer)
    poll.timer = null
  }
  const hidden = () => {
    try {
      const doc =
        (el.node && el.node.ownerDocument) || (typeof document !== 'undefined' ? document : null)
      return !!doc && doc.visibilityState === 'hidden'
    } catch (_) {
      return false
    }
  }
  poll.tick = () => {
    poll.timer = null
    if (!poll.live || r.__workspaceSetupPoll !== poll) return
    if (wsOf(r) !== wsId) return end(false)
    if (Date.now() - poll.since > MAX_MS) return end(true)
    if (hidden()) {
      poll.slept = true
      poll.timer = setTimeout(poll.tick, CAP_MS)
      return
    }
    poll.slept = false
    Promise.resolve(el.call('workspaceSetupRead', r, { sdk: o.sdk || null }))
      .catch(() => null)
      .then((entry) => {
        if (!poll.live || r.__workspaceSetupPoll !== poll) return
        // the loop's next step first, so the effects read the poll as it is
        const job = entry && entry.job
        if (entry && (entry.status === 'error' || entry.code)) poll.fails += 1
        else poll.fails = 0
        if (!entry) {
          // nothing was asked (a gate is closed — the role, the SDK, the
          // trigger): a later prime starts again
          end(false)
        } else if (entry.status === 'none' || poll.fails >= FAILS) {
          end(true)
        } else if (entry.status === 'ok' && (!job || job.status !== 'running')) {
          end(true)
        } else {
          const sig = job
            ? JSON.stringify([job.rev, job.status, job.steps.map((x) => x.key + ':' + x.status)])
            : ''
          if (sig && sig !== poll.sig) {
            poll.sig = sig
            poll.delay = BASE_MS
          } else {
            poll.delay = Math.min(CAP_MS, Math.round(poll.delay * GROW))
          }
          poll.timer = setTimeout(poll.tick, poll.delay)
        }
        try {
          el.call('workspaceSetupEffects', r, { sdk: o.sdk || null })
        } catch (err) {
          console.warn('[setup] effects failed:', err && err.message)
        }
      })
  }
  poll.tick()
  return true
}

// THE EFFECTS of an answer — run after every read and on every change the gate
// follows. Each is single-flight and idempotent:
//   apps    an app step that turned done since the last look → the active
//           workspace record is read again and its apps reach the navigation
//           (the App Store's own install refresh)
//   site    the website step is done → its project, once (the card's name)
//   policy  the signup policy's `firstBuildFree`, once
//   credits the balance, once, when the shell has none yet
//   thread  where the builder shows (`opts.shown`): the job's thread opens
//   build   where the builder shows: the first build, when it may go by itself
//   prompt  an arrival with no setup to build in (no job, a failed one, no
//           read for this person or SDK): the builder held its parked
//           `?prompt=` while that was not known — it goes now, as it always
//           did (functions/addAppPrompt.js takes the park one time)
// → true when the entry is this workspace's.
export function workspaceSetupEffects(root, opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object' || typeof r.update !== 'function') return false
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (el.context && el.context.functions) || {}
  if (
    typeof fns.addAppPromptFlush === 'function' &&
    el.call('workspaceSetupOwnsBuild', r) === false &&
    (el.call('workspaceSetupWanted', r) || {}).arrived
  ) {
    Promise.resolve(el.call('addAppPromptFlush', r)).catch(() => null)
  }
  const entry = r._workspaceSetup
  if (!wsId || !entry || entry.ws !== wsId || entry.status !== 'ok' || !entry.job) return false
  const job = entry.job
  const done = job.steps
    .filter((x) => x.key.indexOf('app:') === 0 && x.status === 'done')
    .map((x) => x.key)
  const seen = Array.isArray(entry.apps) ? entry.apps : null
  // the first look refreshes once when apps are done already (the shell may
  // have read the workspace before they landed); every later done app is new
  // to the navigation
  if (!seen) {
    r.update({ _workspaceSetup: { ...entry, apps: done } }, { preventFetch: true })
    if (done.length) {
      Promise.resolve(el.call('workspaceSetupRefreshApps', r, { sdk: o.sdk || null })).catch(
        () => null
      )
    }
  } else if (done.some((k) => seen.indexOf(k) < 0)) {
    r.update({ _workspaceSetup: { ...r._workspaceSetup, apps: done } }, { preventFetch: true })
    Promise.resolve(el.call('workspaceSetupRefreshApps', r, { sdk: o.sdk || null })).catch(
      () => null
    )
  }
  const site = job.steps.find((x) => x.key === 'website')
  const siteSettled =
    !!site && (site.status === 'done' || (site.status === 'skipped' && site.reason === 'already'))
  const siteId = siteSettled
    ? String((site.detail && site.detail.projectId) || job.projectId || '')
    : ''
  const cur = r._workspaceSetup
  if (siteId && !(cur.site && cur.site.projectId === siteId)) {
    Promise.resolve(el.call('workspaceSetupSiteRead', r, { sdk: o.sdk || null })).catch(() => null)
  }
  if (cur.policy === undefined) {
    Promise.resolve(el.call('workspaceSetupPolicyRead', r, { sdk: o.sdk || null })).catch(
      () => null
    )
  }
  // the person's record again, right before a free build is promised
  if (job.status === 'done' && cur.policy === true && !(cur.me && cur.me.checked)) {
    Promise.resolve(el.call('workspaceSetupMeRead', r, { sdk: o.sdk || null })).catch(() => null)
  }
  if (!r._creditGuard && !r.__workspaceSetupCredits && typeof fns.loadCreditGuard === 'function') {
    r.__workspaceSetupCredits = true
    Promise.resolve(el.call('loadCreditGuard', r)).catch(() => null)
  }
  if (o.shown) {
    Promise.resolve(el.call('workspaceSetupOpenThread', r)).catch(() => null)
    if (el.call('workspaceSetupFirstBuild', r) === 'auto') el.call('workspaceSetupSendBuild', r)
  }
  return true
}

// THE BUILDER'S PRIME — the gate's onCreate / onUpdate / visibility change
// (components/WorkspaceSetupGate.js). `el` is the gate on the builder page:
// its scope says whether the builder is the route (`addAppThreadInUrl`) or the
// home's Build tab. The arrival is read on the first call; the poll starts
// once the read can answer; the effects run with `shown` when the builder is
// on screen (the route, or the home's Build tab showing). → true when a
// setup entry belongs to this workspace.
export function workspaceSetupPrime(el, s, opts) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root) || null
  if (!host || typeof host.call !== 'function' || !root) return false
  const o = opts && typeof opts === 'object' ? opts : {}
  const fns = (host.context && host.context.functions) || {}
  const inUrl = !(host.scope && host.scope.addAppThreadInUrl === false)
  if (!host.__workspaceSetupArrived) {
    host.__workspaceSetupArrived = true
    host.call('workspaceSetupArrive', root, { url: inUrl })
  }
  const shown = inUrl
    ? true
    : typeof fns.homeBuildShown === 'function'
      ? !!host.call('homeBuildShown', root)
      : false
  host.call('workspaceSetupPoll', root, { wake: !!o.wake })
  return !!host.call('workspaceSetupEffects', root, { shown })
}

// RETRY — the failed lines again (`startWorkspaceSetup(id, { retry: true })`,
// owner / admin only — the button shows only for them), then a fresh poll.
// → a promise of true when the server took it.
export function workspaceSetupRetry(opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = (o.root && typeof o.root === 'object' && o.root) || (el && el.state && el.state.root)
  if (!r || typeof r.update !== 'function') return Promise.resolve(false)
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (el.context && el.context.functions) || {}
  const entry = r._workspaceSetup
  if (!wsId || !entry || entry.ws !== wsId || entry.retrying) return Promise.resolve(false)
  if (typeof fns.currentUserIsAdmin !== 'function' || !el.call('currentUserIsAdmin', r)) {
    return Promise.resolve(false)
  }
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.startWorkspaceSetup !== 'function') return Promise.resolve(false)
  r.update({ _workspaceSetup: { ...entry, retrying: true } }, { preventFetch: true })
  const settle = (ok) => {
    const cur = r._workspaceSetup
    if (cur && cur.ws === wsId) {
      r.update({ _workspaceSetup: { ...cur, retrying: false } }, { preventFetch: true })
    }
    if (ok) el.call('workspaceSetupPoll', r, { force: true, sdk: o.sdk || null })
    return ok
  }
  return Promise.resolve()
    .then(() => sdk.startWorkspaceSetup(wsId, { retry: true }))
    .then(
      () => settle(true),
      (err) => {
        console.warn('[setup] retry refused:', String((err && (err.status || err.code)) || ''))
        return settle(false)
      }
    )
}

// THE NAVIGATION — an app step turned done: the active workspace record is
// read again and its apps hot-activate (the App Store's own install refresh:
// fetchFullWorkspaceRecord → setActiveWorkspaceRecord → reactivateWorkspaceModule,
// bounded at 20 s). One refresh at a time; a change meanwhile asks one more.
export function workspaceSetupRefreshApps(root, opts) {
  const WAIT_MS = 20000
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object') return Promise.resolve(false)
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (el.context && el.context.functions) || {}
  if (!wsId || typeof fns.fetchFullWorkspaceRecord !== 'function') return Promise.resolve(false)
  if (r.__workspaceSetupApps) {
    r.__workspaceSetupApps.again = true
    return r.__workspaceSetupApps.promise
  }
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk) return Promise.resolve(false)
  const flight = { again: false, promise: null }
  const stillHere = () => {
    const now = r.workspace
    return String((now && (now.id || now._id)) || '') === wsId
  }
  flight.promise = Promise.resolve()
    .then(() => el.call('fetchFullWorkspaceRecord', sdk, wsId))
    .then(async (full) => {
      if (!full || !stillHere()) return false
      el.call('setActiveWorkspaceRecord', r, full, { sdk, reason: 'workspaceSetup' })
      if (typeof fns.reactivateWorkspaceModule === 'function') {
        const settings = (full.settings && typeof full.settings === 'object' && full.settings) || {}
        let timer = null
        const bound = new Promise((resolve) => {
          timer = setTimeout(resolve, WAIT_MS)
        })
        try {
          await Promise.race([
            // QUIET (R2c-m2): the workspace building itself in front of the
            // person is no "Retrying workspace loading"; a failure still shows.
            Promise.resolve(
              el.call(
                'reactivateWorkspaceModule',
                null,
                {
                  workspaceApps: Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
                },
                { quiet: true }
              )
            ),
            bound
          ])
        } finally {
          clearTimeout(timer)
        }
      }
      return true
    })
    .catch((err) => {
      console.warn('[setup] the navigation did not refresh:', err && err.message)
      return false
    })
    .then((ok) => {
      if (r.__workspaceSetupApps === flight) r.__workspaceSetupApps = null
      if (flight.again && stillHere()) el.call('workspaceSetupRefreshApps', r, o)
      return ok
    })
  r.__workspaceSetupApps = flight
  return flight.promise
}

// THE WEBSITE CARD's facts — the website step's project, read once through the
// SDK (`getProject`): its owner and key (the canvas link) and its own name.
export function workspaceSetupSiteRead(root, opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return Promise.resolve(null)
  const entry = r._workspaceSetup
  const job = entry && entry.job
  const step = job && job.steps.find((x) => x.key === 'website')
  // done (a new website) or skipped `already` (the workspace's own, adopted)
  const settled =
    !!step && (step.status === 'done' || (step.status === 'skipped' && step.reason === 'already'))
  const projectId = settled
    ? String((step.detail && step.detail.projectId) || job.projectId || '')
    : ''
  if (!projectId) return Promise.resolve(null)
  if (r.__workspaceSetupSite === projectId) return Promise.resolve(null)
  const fns = (el.context && el.context.functions) || {}
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.getProject !== 'function') return Promise.resolve(null)
  r.__workspaceSetupSite = projectId
  const ws = entry.ws
  return Promise.resolve()
    .then(() => sdk.getProject(projectId))
    .then(
      (res) => {
        const p =
          res && typeof res === 'object' && res.data && typeof res.data === 'object'
            ? res.data
            : res
        if (!p || typeof p !== 'object') return null
        const owner =
          typeof fns.ownerSlug === 'function' ? String(el.call('ownerSlug', p) || '') : ''
        const key =
          typeof fns.projectKey === 'function' ? String(el.call('projectKey', p) || '') : ''
        return { projectId, owner, key, name: String(p.name || '').trim() }
      },
      () => null
    )
    .then((site) => {
      const cur = r._workspaceSetup
      if (!site || !cur || cur.ws !== ws) {
        if (!site && r.__workspaceSetupSite === projectId) r.__workspaceSetupSite = ''
        return null
      }
      r.update({ _workspaceSetup: { ...cur, site } }, { preventFetch: true })
      return site
    })
}

// THE SIGNUP POLICY's `firstBuildFree` (`GET /core/auth/signup-policy` →
// `{ password, firstBuildFree, signupCredits }`), read once. An SDK without
// the read leaves it unknown (null) — the waiver is then never assumed.
export function workspaceSetupPolicyRead(root, opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return Promise.resolve(null)
  const entry = r._workspaceSetup
  if (!entry || entry.policy !== undefined || r.__workspaceSetupPolicy) {
    return Promise.resolve(null)
  }
  const fns = (el.context && el.context.functions) || {}
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  const ws = entry.ws
  const store = (policy) => {
    r.__workspaceSetupPolicy = null
    const cur = r._workspaceSetup
    if (cur && cur.ws === ws) {
      r.update({ _workspaceSetup: { ...cur, policy } }, { preventFetch: true })
    }
    return policy
  }
  if (!sdk || typeof sdk.getSignupPolicy !== 'function') return Promise.resolve(store(null))
  r.__workspaceSetupPolicy = true
  return Promise.resolve()
    .then(() => sdk.getSignupPolicy())
    .then(
      (res) => {
        const d =
          res && typeof res === 'object' && res.data && typeof res.data === 'object'
            ? res.data
            : res
        return store(d && typeof d.firstBuildFree === 'boolean' ? d.firstBuildFree : null)
      },
      () => store(null)
    )
}

// THE THREAD — the builder opens the job's first thread (its `_ax_` scope, no
// app bound: a build turn there carries the build surface). Only once the
// builder's transport is open (the AI service answers then), and never over a
// thread the person chose on a load that is no arrival. On an arrival the
// setup thread wins over a remembered thread and an app the builder held.
// → a promise of true when the thread is the builder's.
export function workspaceSetupOpenThread(root) {
  const RECENT_MS = 10 * 60 * 1000
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return Promise.resolve(false)
  const entry = r._workspaceSetup
  const job = entry && entry.status === 'ok' ? entry.job : null
  const id = job ? String(job.threadId || '') : ''
  if (!id) return Promise.resolve(false)
  const ended = Date.parse(String(job.finishedAt || ''))
  const recent =
    job.status === 'running' || (Number.isFinite(ended) && Date.now() - ended <= RECENT_MS)
  // an older job is history — unless this page is a Make it real arrival
  if (!recent && !entry.arrived) return Promise.resolve(false)
  if (String(r._ax_activeSessionId || '') === id) {
    if (entry.opened !== id) {
      r.update({ _workspaceSetup: { ...entry, opened: id } }, { preventFetch: true })
    }
    return Promise.resolve(true)
  }
  // opened once already: the person moved on to another thread or app
  if (entry.opened === id || entry.opening) return Promise.resolve(false)
  if (r.bridgeStatus !== 'open') return Promise.resolve(false)
  if (!entry.arrived && (r._ax_activeSessionId || r._addAppProjectId)) return Promise.resolve(false)
  r.update({ _workspaceSetup: { ...entry, opening: true } }, { preventFetch: true })
  if (r._addAppProjectId) {
    r.__addAppHydratedThreadId = null
    r.update({ _addAppProjectId: null, _addAppRendered: false }, { preventFetch: true })
  }
  const settle = (ok) => {
    const cur = r._workspaceSetup
    if (cur && cur.ws === entry.ws) {
      r.update(
        { _workspaceSetup: { ...cur, opening: false, opened: ok ? id : cur.opened } },
        { preventFetch: true }
      )
    }
    return ok
  }
  return Promise.resolve()
    .then(() => el.call('switchSession', id, 'addapp'))
    .then(
      () => settle(String(r._ax_activeSessionId || '') === id),
      (err) => {
        console.warn('[setup] the first thread did not open:', err && err.message)
        return settle(false)
      }
    )
}

// THE PERSON'S OWN CLAIM of the first-build waiver — ONE place for the field
// (server feat/onboarding-r1 81b37cb02: `User.firstBuildFree = { workspace,
// claimedAt }`, absent = never claimed; GET /core/auth/me → `data.user`). The
// fresh read made right before a free build is promised (workspaceSetupMeRead
// — the boot's `root.user` can predate a claim in another workspace) wins over
// the boot's record. → { workspace, claimedAt } | null
export function workspaceSetupUserClaim(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r) return null
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const entry =
    r._workspaceSetup && typeof r._workspaceSetup === 'object' && r._workspaceSetup.ws === wsId
      ? r._workspaceSetup
      : null
  const fresh = entry && entry.me && entry.me.fresh ? entry.me : null
  const u = fresh ? fresh.user : r.user && typeof r.user === 'object' ? r.user : null
  const claim =
    u && u.firstBuildFree && typeof u.firstBuildFree === 'object' ? u.firstBuildFree : null
  return claim && claim.claimedAt
    ? { workspace: String(claim.workspace || ''), claimedAt: claim.claimedAt }
    : null
}

// The person's record, read again right before a free build is promised
// (`sdk.getMe` — the shell's own auth/me read; the server caches it ~5 s): a
// claim made in another workspace since this tab booted closes the waiver
// here. Once per entry; only the waiver field is kept.
//   entry.me = { checked: true, fresh, user: { firstBuildFree } | null }
export function workspaceSetupMeRead(root, opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return Promise.resolve(null)
  const entry = r._workspaceSetup
  if (!entry || (entry.me && entry.me.checked) || r.__workspaceSetupMe) {
    return Promise.resolve(null)
  }
  const fns = (el.context && el.context.functions) || {}
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  const ws = entry.ws
  const store = (user) => {
    r.__workspaceSetupMe = null
    const cur = r._workspaceSetup
    if (!cur || cur.ws !== ws) return null
    const me = {
      checked: true,
      fresh: !!user,
      user: user ? { firstBuildFree: user.firstBuildFree || null } : null
    }
    r.update({ _workspaceSetup: { ...cur, me } }, { preventFetch: true })
    return me
  }
  if (!sdk || typeof sdk.getMe !== 'function') return Promise.resolve(store(null))
  r.__workspaceSetupMe = true
  return Promise.resolve()
    .then(() => sdk.getMe())
    .then(
      (res) => {
        const me = (res && (res.user || (res.data && res.data.user))) || res
        return store(me && typeof me === 'object' && (me._id || me.id) ? me : null)
      },
      () => store(null)
    )
}

// THE FIRST BUILD's state, for the thread the builder shows:
//   ''        nothing to build here (no recent job, another thread, no plan)
//   'wait'    the setup runs, or a fact is still being read
//   'auto'    it goes by itself now (the waiver is open, or credits arrived
//             for a build that waited on them)
//   'ask'     the person presses Build (credits, no waiver)
//   'credits' no credits: the claim / top-up path first
//   'sent'    the thread holds the person's first message (or this page sent it)
export function workspaceSetupFirstBuild(root) {
  const RECENT_MS = 10 * 60 * 1000
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object') return ''
  const w = r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const wsId = String((w && (w.id || w._id)) || '')
  const entry = r._workspaceSetup
  const job = entry && entry.ws === wsId && entry.status === 'ok' ? entry.job : null
  const id = job ? String(job.threadId || '') : ''
  if (!id || String(r._ax_activeSessionId || '') !== id) return ''
  const msgs = Array.isArray(r._ax_chatMessages) ? r._ax_chatMessages : []
  if (entry.build === 'sent' || msgs.some((m) => m && m.role === 'user')) return 'sent'
  if (job.status === 'running') return 'wait'
  if (job.status !== 'done') return ''
  const ended = Date.parse(String(job.finishedAt || ''))
  const recent = Number.isFinite(ended) && Date.now() - ended <= RECENT_MS
  if (!recent && !entry.arrived && !entry.build) return ''
  if (!el.call('workspaceSetupBuildText', r)) return ''
  // The waiver rule (server FirstBuildFreeService.admit): FREE when the policy
  // says `firstBuildFree: true`, this person never claimed it, and this
  // workspace's marker is absent — a marker naming THIS thread means the first
  // build already went here; any other marker closes it (a teammate pays).
  const settings = w && w.settings && typeof w.settings === 'object' ? w.settings : {}
  const marker =
    settings.firstBuildFree && typeof settings.firstBuildFree === 'object'
      ? settings.firstBuildFree
      : null
  if (marker && String(marker.threadId || '') === id) return 'sent'
  if (entry.policy === undefined) return 'wait'
  if (entry.policy === true && !marker) {
    // a free build is promised only after the person's record is read again
    if (!(entry.me && entry.me.checked)) return 'wait'
    if (!el.call('workspaceSetupUserClaim', r)) return 'auto'
  }
  const fns = (el.context && el.context.functions) || {}
  const guard = typeof fns.creditGuardState === 'function' ? el.call('creditGuardState', r) : null
  if (!guard || !guard.loaded) return 'wait'
  if (guard.status === 'exhausted') return 'credits'
  // a build that waited on credits goes once they arrive
  if (entry.build === 'held' && typeof guard.total === 'number' && guard.total > 0) return 'auto'
  return 'ask'
}

// The first build's words: the person's idea (`concepts[0].text` of the plan
// note), else one line made from the plan in the shell's language. The model
// never sees the notes, so the message carries its own ask. → '' when the
// thread holds no plan.
export function workspaceSetupBuildText(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const msgs = r && Array.isArray(r._ax_chatMessages) ? r._ax_chatMessages : []
  const row = msgs.find((m) => m && m.metadata && m.metadata.kind === 'note' && m.metadata.plan)
  const plan = row && typeof row.metadata.plan === 'object' ? row.metadata.plan : null
  if (!plan) return ''
  const concept = Array.isArray(plan.concepts) ? plan.concepts[0] : null
  const idea = concept && typeof concept.text === 'string' ? concept.text.trim() : ''
  if (idea) return idea
  const company = plan.company && typeof plan.company === 'object' ? plan.company : {}
  const name = typeof company.name === 'string' ? company.name.trim() : ''
  const tagline = typeof company.tagline === 'string' ? company.tagline.trim() : ''
  const t = (k) => String(el.call('polyglot', k) || '')
  if (name && tagline) {
    return t('workspace.setup.buildFromPlan')
      .replace('{company}', name)
      .replace('{tagline}', tagline)
  }
  if (name) return t('workspace.setup.buildFromName').replace('{company}', name)
  return t('workspace.setup.buildPlain')
}

// SEND the first build — once per thread and page: the state says it may go
// (or the person pressed Build: `opts.pressed`), the builder shows the setup
// thread with no app bound, and its transport is open. The entry is marked
// `sent` BEFORE the send, so no second caller can send it again. → true when
// it was sent.
export function workspaceSetupSendBuild(root, opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return false
  const state = el.call('workspaceSetupFirstBuild', r)
  if (!(state === 'auto' || (o.pressed && state === 'ask'))) return false
  if (r.bridgeStatus !== 'open' || r._addAppProjectId) return false
  const entry = r._workspaceSetup
  const id = String(entry.job.threadId)
  if (r.__workspaceSetupSent === id) return false
  const text = el.call('workspaceSetupBuildText', r)
  if (!text) return false
  r.__workspaceSetupSent = id
  r.update({ _workspaceSetup: { ...entry, build: 'sent' } }, { preventFetch: true })
  el.call('sendPrompt', text, 'addapp')
  return true
}

// The person pressed Build (`ask`), or opened the claim path while the
// workspace has no credits (`credits` → the build waits and goes once credits
// arrive).
export function workspaceSetupBuildPress(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function') return false
  const state = el.call('workspaceSetupFirstBuild', r)
  if (state === 'credits') {
    const entry = r._workspaceSetup
    if (entry.build !== 'held') {
      r.update({ _workspaceSetup: { ...entry, build: 'held' } }, { preventFetch: true })
    }
    return false
  }
  return el.call('workspaceSetupSendBuild', r, { pressed: true })
}

// THE ONE BUILD PLACE (CORE r1: the waiver is claimed by the first build turn
// in ANY thread). While a setup job is this workspace's — running, done inside
// RECENT_MS, or done and this page is a Make it real arrival — its first
// thread is the only place the idea is built, so the builder's `?prompt=`
// hand-off (functions/addAppPrompt.js) does not send:
//   true   the setup owns the first build — the parked prompt is not sent
//   null   not known yet: this load is an arrival and the read can still
//          answer (it reads, or a failed read is asked again) — keep the park
//   false  no setup to build in (no job, a failed job, an older job on a load
//          that is no arrival, no read for this person or SDK) — the hand-off
//          sends as it always did
export function workspaceSetupOwnsBuild(root) {
  const RECENT_MS = 10 * 60 * 1000
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object') return false
  const fns = (el && el.context && el.context.functions) || {}
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const entry = r._workspaceSetup && r._workspaceSetup.ws === wsId ? r._workspaceSetup : null
  if (entry && entry.status === 'ok' && entry.job) {
    const job = entry.job
    if (job.status === 'running') return true
    if (job.status !== 'done') return false
    const ended = Date.parse(String(job.finishedAt || ''))
    return (Number.isFinite(ended) && Date.now() - ended <= RECENT_MS) || !!entry.arrived
  }
  if (entry && entry.status === 'none') return false
  if (entry && entry.status === 'reading') return null
  if (entry && entry.status === 'error') {
    // a failed read is asked again while the poll runs (three in a row end it)
    const poll = r.__workspaceSetupPoll
    return poll && poll.ws === wsId && poll.live ? null : false
  }
  // nothing read yet: only an arrival waits for the builder's read
  const wanted = el.call('workspaceSetupWanted', r) || {}
  if (!wanted.arrived) return false
  if (wsId && r.workspaceReady) {
    if (typeof fns.currentUserIsAdmin !== 'function' || !el.call('currentUserIsAdmin', r)) {
      return false
    }
  }
  let sdk = null
  if (typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.getWorkspaceSetup !== 'function') return false
  return null
}

// THE READY SCREEN's hand-off (functions/onboardingReady.js opens it when the
// setup ends — the onboarding agent's gate asks this and follows
// `_workspaceSetup`): hold while this arrival's setup is being read or runs,
// and — on the builder — until its first build is sent or waits on the
// person. Never past RECENT_MS (the arrival's own window).
// → true while the screen should wait.
export function workspaceSetupHoldsReady(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object') return false
  const owns = el.call('workspaceSetupOwnsBuild', r)
  if (owns === null) return true
  if (owns !== true) return false
  const entry = r._workspaceSetup
  const job = entry && entry.job
  if (!job || job.status === 'running') return true
  if (!job.threadId) return false
  // the first build goes only where the builder shows its thread
  const onBuilder = /\/add-app\/?$/.test(String(r.currentPath || '').split('?')[0])
  if (!onBuilder) return false
  const state = el.call('workspaceSetupFirstBuild', r)
  return state === 'wait' || state === 'auto'
}

// THE WAY BACK — the site the person came from: the onboarding record's
// `from` (when Make it real writes one), else the arrival's referrer. Only an
// https platform host (http on localhost) that is not this shell; only its
// origin and path (no query, no hash — no words of the person's ever ride a
// link). → { href, host } | null
export function workspaceSetupBackHref(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r !== 'object') return null
  const fns = (el && el.context && el.context.functions) || {}
  const prefs = r.userPrefs && typeof r.userPrefs === 'object' ? r.userPrefs : null
  const rec =
    prefs && prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  const entry =
    r._workspaceSetup && typeof r._workspaceSetup === 'object' ? r._workspaceSetup : null
  const here = typeof window !== 'undefined' && window.location ? window.location.origin : ''
  for (const raw of [rec && rec.from, entry && entry.back]) {
    if (typeof raw !== 'string' || !raw) continue
    try {
      const url = new URL(raw)
      const local = url.hostname === 'localhost' || url.hostname.endsWith('.localhost')
      if (!(url.protocol === 'https:' || (local && url.protocol === 'http:'))) continue
      if (url.origin === here) continue
      if (typeof fns.isPlatformHost !== 'function' || !el.call('isPlatformHost', url.hostname)) {
        continue
      }
      return { href: url.origin + url.pathname, host: url.hostname }
    } catch (_) {
      /* not a URL */
    }
  }
  return null
}

// One app's name as the App Store names it: the catalogue's own record, else
// the shipped `marketplace.app.<camelKey>.name`, else the key itself (never
// the Blueprint's words — an id is all the plan holds).
export function workspaceSetupAppName(root, id) {
  const el = this
  const ref = String(id || '').toLowerCase()
  const key = ref.split('/').pop() || ''
  if (!key) return { title: '', icon: '' }
  const fns = (el && el.context && el.context.functions) || {}
  const byId =
    typeof fns.onboardingCatalogApps === 'function'
      ? (el.call('onboardingCatalogApps', root) || {}).byId || {}
      : {}
  const app = byId[ref]
  if (app && app.title) return { title: app.title, icon: app.icon || '' }
  const named =
    typeof fns.storeItemText === 'function'
      ? el.call('storeItemText', { key, name: '' }, 'name')
      : ''
  return { title: String(named || '').trim() || key, icon: '' }
}

// THE VIEW of the setup block above the builder's messages — pure over the
// root. Shown while the builder shows the job's thread and the job runs or
// ended inside RECENT_MS. Every key a part reads is always present.
//   lines  one row per step a person can see, the setup line's own row shape
//          ({ key, id, title, icon, state, done, failed, stateKey }); the
//          `onboarding` and `thread` steps are the setup's own business and a
//          skipped step did nothing, so neither shows
export function workspaceSetupView(root) {
  const RECENT_MS = 10 * 60 * 1000
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const out = {
    show: false,
    pending: false,
    status: '',
    title: '',
    lines: [],
    failText: '',
    retryable: false,
    retrying: false,
    siteShow: false,
    siteTitle: '',
    creditsShow: false,
    creditsText: '',
    backShow: false,
    backHref: '',
    backText: '',
    build: '',
    buildShow: false,
    buildText: '',
    buildAction: '',
    claimShow: false,
    claimText: ''
  }
  if (!r || typeof r !== 'object') return out
  const w = r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const wsId = String((w && (w.id || w._id)) || '')
  const entry = r._workspaceSetup && r._workspaceSetup.ws === wsId ? r._workspaceSetup : null
  if (!entry) return out
  const t = (k) => String(el.call('polyglot', k) || '')
  const job = entry.status === 'ok' ? entry.job : null
  // reading on an arrival (or opening its thread): the builder's empty state
  // waits instead of flashing under the setup
  if (entry.arrived && (entry.status === 'reading' || entry.opening)) out.pending = true
  if (!job) return out
  // no thread yet: the `thread` step still runs (a `200` can answer mid-way),
  // or it failed (`seed_failed`) — then the setup shows on the builder's own
  // blank thread, with its Try again (a retry writes the thread)
  const threadStep = job.steps.find((x) => x.key === 'thread')
  const threadOpen =
    !!threadStep && (threadStep.status === 'pending' || threadStep.status === 'running')
  const bare =
    !job.threadId &&
    entry.arrived &&
    !r._ax_activeSessionId &&
    (job.status === 'failed' || !threadOpen)
  if (!job.threadId && !bare) {
    if (entry.arrived && threadOpen && job.status === 'running') out.pending = true
    return out
  }
  if (!bare && String(r._ax_activeSessionId || '') !== job.threadId) {
    if (entry.arrived && !entry.opened && job.status === 'running') out.pending = true
    return out
  }
  const ended = Date.parse(String(job.finishedAt || ''))
  const recent =
    job.status === 'running' || (Number.isFinite(ended) && Date.now() - ended <= RECENT_MS)
  // an older job is history — unless this page is a Make it real arrival
  if (!recent && !entry.arrived && !entry.build) return out
  out.show = true
  out.status = job.status
  const wsName = String((w && (w.name || w.displayName)) || '').trim()
  // literal keys, one per state and per named / unnamed workspace
  const TITLES = {
    running: ['workspace.setup.title', 'workspace.setup.titleNoName'],
    failed: ['workspace.setup.titleFailed', 'workspace.setup.titleFailedNoName'],
    done: ['workspace.setup.titleDone', 'workspace.setup.titleDoneNoName']
  }
  const pair = TITLES[job.status] || TITLES.done
  out.title = wsName ? t(pair[0]).replace('{workspace}', wsName) : t(pair[1])
  const LABELS = {
    name: ['workspace.setup.stepName', 'edit'],
    language: ['workspace.setup.stepLanguage', 'language'],
    theme: ['workspace.setup.stepTheme', 'theme'],
    preset: ['workspace.setup.stepPreset', 'folder'],
    website: ['workspace.setup.stepWebsite', 'globe']
  }
  // the reason of a failed line in words; an app the environment does not
  // carry is "Coming soon", never a failure
  const REASONS = {
    missing_required_dependencies: 'workspace.setup.needsApps',
    install_refused: 'workspace.setup.refused',
    forbidden: 'workspace.setup.refused',
    free_cap_reached: 'workspace.setup.siteCap',
    no_app_landed: 'workspace.setup.noApps'
  }
  let failed = 0
  job.steps.forEach((step, i) => {
    const app = step.key.indexOf('app:') === 0 ? step.key.slice(4) : ''
    const label = LABELS[step.key]
    if (!app && !label) return
    if (step.status === 'skipped' && !(step.key === 'website' && step.reason === 'already')) return
    // the inline steps ran before the person arrived: shown once they settled
    const inline = !app && step.key !== 'preset' && step.key !== 'website'
    if (inline && (step.status === 'pending' || step.status === 'running')) return
    const named = app ? el.call('workspaceSetupAppName', r, app) || {} : null
    const soon = !!app && step.status === 'failed' && step.reason === 'app_not_found'
    const isDone = step.status === 'done' || step.status === 'skipped'
    const isFailed = step.status === 'failed' && !soon
    if (isFailed) failed++
    const waiting = step.status === 'pending' || step.status === 'running'
    const stateKey = soon
      ? 'marketplace.preset.lineComingSoon'
      : isFailed
        ? REASONS[step.reason] ||
          (app ? 'marketplace.preset.lineFailed' : 'workspace.setup.lineFailed')
        : waiting
          ? app
            ? 'marketplace.preset.linePending'
            : 'workspace.setup.lineWorking'
          : app
            ? step.detail && step.detail.already
              ? 'marketplace.preset.lineSkipped'
              : 'marketplace.preset.lineInstalled'
            : 'workspace.setup.lineReady'
    out.lines.push({
      key: 'setup-line-' + (i + 1),
      id: step.key,
      title: app ? String(named.title || '') : t(label[0]),
      icon: app ? String(named.icon || '') || 'blocks' : label[1],
      state: step.status,
      done: isDone,
      failed: isFailed,
      stateKey
    })
  })
  const fns = (el.context && el.context.functions) || {}
  const admin = typeof fns.currentUserIsAdmin === 'function' && !!el.call('currentUserIsAdmin', r)
  if (job.status === 'failed') {
    out.failText = t('workspace.setup.failedStart')
    out.retryable = admin && job.reason !== 'no_blueprint' && job.reason !== 'workspace_not_found'
  } else if (bare) {
    out.failText = t('workspace.setup.threadFailed')
    out.retryable = admin && job.status === 'done'
  } else if (job.status === 'done' && failed) {
    out.retryable = admin
  }
  out.retrying = !!entry.retrying
  const site = job.steps.find((x) => x.key === 'website')
  if (
    site &&
    (site.status === 'done' || (site.status === 'skipped' && site.reason === 'already')) &&
    entry.site &&
    entry.site.owner &&
    entry.site.key
  ) {
    out.siteShow = true
    out.siteTitle = entry.site.name || t('workspace.setup.stepWebsite')
  }
  // free: the waiver rule (workspaceSetupFirstBuild) — open, or this thread's
  // own (its later turns stay free inside the server's cap)
  const guard = typeof fns.creditGuardState === 'function' ? el.call('creditGuardState', r) : null
  const claim = el.call('workspaceSetupUserClaim', r)
  const settings = w && w.settings && typeof w.settings === 'object' ? w.settings : {}
  const marker =
    settings.firstBuildFree && typeof settings.firstBuildFree === 'object'
      ? settings.firstBuildFree
      : null
  const mine = !!marker && !!job.threadId && String(marker.threadId || '') === job.threadId
  const free =
    entry.policy === true && ((!marker && !claim) || (mine && (!claim || claim.workspace === wsId)))
  if (free) {
    out.creditsShow = true
    out.creditsText = t('workspace.setup.creditsFree')
  } else if (guard && guard.loaded && typeof guard.total === 'number') {
    out.creditsShow = true
    out.creditsText = t(
      guard.total === 1 ? 'workspace.setup.creditsOne' : 'workspace.setup.credits'
    ).replace('{count}', Number(guard.total).toLocaleString('en-US'))
  }
  const back = el.call('workspaceSetupBackHref', r)
  if (back) {
    out.backShow = true
    out.backHref = back.href
    out.backText = t('workspace.setup.back').replace('{site}', back.host)
  }
  const build = el.call('workspaceSetupFirstBuild', r)
  out.build = build
  if (build === 'wait' && job.status === 'running') {
    out.buildShow = true
    out.buildText = t('workspace.setup.buildAfter')
  } else if (build === 'auto' || (build === 'wait' && job.status === 'done')) {
    out.buildShow = true
    out.buildText = t('workspace.setup.buildStarting')
  } else if (build === 'ask') {
    out.buildShow = true
    out.buildText = t('workspace.setup.buildAsk')
    out.buildAction = t('workspace.setup.buildAction')
  } else if (build === 'credits') {
    out.buildShow = true
    out.buildText = t(admin ? 'workspace.setup.buildCredits' : 'workspace.setup.buildCreditsMember')
    out.claimShow = admin
    out.claimText = t('workspace.setup.claim')
  }
  return out
}

// THE PLAN CARD — the plan note's Blueprint snapshot, in the shell's language:
// the company and its line, the industry (the preset's shipped name), the
// team, the apps (the catalogue's names), the website and the idea. Every key
// a part reads is always present. `plan` is the note's `metadata.plan`.
export function workspaceSetupPlanView(plan) {
  const el = this
  const out = {
    show: false,
    title: '',
    name: '',
    tagline: '',
    facts: '',
    apps: [],
    appsText: '',
    siteText: '',
    ideaLabel: '',
    idea: ''
  }
  const p = plan && typeof plan === 'object' && !Array.isArray(plan) ? plan : null
  if (!p) return out
  const root = (el && el.state && el.state.root) || null
  const t = (k) => String(el.call('polyglot', k) || '')
  const text = (v) => (typeof v === 'string' ? v.trim() : '')
  out.show = true
  out.title = t('workspace.setup.planTitle')
  const company = p.company && typeof p.company === 'object' ? p.company : {}
  out.name = text(company.name)
  out.tagline = text(company.tagline)
  const facts = []
  // the industry is a preset key: its name is the preset's own (the App
  // Store's resolver), never the key
  const industry = text(p.industry)
  const fns = (el && el.context && el.context.functions) || {}
  if (industry && industry !== 'custom' && typeof fns.workspacePresetText === 'function') {
    const named = String(el.call('workspacePresetText', industry, 'name') || '').trim()
    if (named) facts.push(named)
  }
  const team = Number(p.team)
  if (Number.isInteger(team) && team > 0) {
    facts.push(
      team === 1
        ? t('workspace.setup.planTeamOne')
        : t('workspace.setup.planTeam').replace('{count}', team.toLocaleString('en-US'))
    )
  }
  out.facts = facts.join(' · ')
  const ids = Array.isArray(p.apps) ? p.apps.filter((x) => typeof x === 'string' && x) : []
  out.apps = ids.map((id, i) => {
    const named = el.call('workspaceSetupAppName', root, id) || {}
    return {
      key: 'plan-app-' + (i + 1),
      title: String(named.title || ''),
      icon: String(named.icon || '') || 'blocks'
    }
  })
  out.appsText = out.apps.length ? t('workspace.setup.planApps') : ''
  const site = p.site && typeof p.site === 'object' ? p.site : null
  const siteName = site ? text(site.name) : ''
  out.siteText = siteName ? t('workspace.setup.planSite').replace('{site}', siteName) : ''
  const concept = Array.isArray(p.concepts) ? p.concepts[0] : null
  out.idea = concept ? text(concept.text) : ''
  out.ideaLabel = out.idea ? t('workspace.setup.planIdea') : ''
  return out
}

// THE PLAN of the thread a list of rows holds (a builder or a main thread):
// the folded summary row or the plan row carries it. → the plan | null
export function workspaceSetupPlanOf(list) {
  const rows = Array.isArray(list) ? list : []
  const row = rows.find((m) => m && m.metadata && m.metadata.kind === 'note' && m.metadata.plan)
  return row && typeof row.metadata.plan === 'object' ? row.metadata.plan : null
}

// The website card's press: the project's canvas (where Publish is), else the
// Websites view.
export function workspaceSetupOpenSite(el) {
  const host = el || this
  const root = host && host.state && host.state.root
  if (!root) return false
  const entry = root._workspaceSetup
  const site = entry && entry.site
  const href =
    site && site.owner && site.key ? host.call('projectHref', root, site.owner, site.key) : null
  host.call('openInSlot', href || '/studio', host)
  return true
}
